import React, {
  useMemo, useState, useEffect, useRef,
} from 'react';
import {
  LineChart, Line, XAxis, YAxis, Text, Tooltip, Rectangle,
} from 'recharts';
import styled from 'styled-components';
import Card, { CardBody } from '../Card';
import { convertDataKey, convertData, getDomainColor } from './utils';

// SETTINGS
const lineHeight = 44;
const zeroMargin = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 30,
};

const XAxisTick = ({
  x, y, width, height, payload, index,
}) => (
  <>
    <Rectangle
      x={x - (index === 0 ? 100 : 0)}
      y={y - height}
      width={width * 2}
      height={height}
      fill="#343a40"
    />
    <Text x={x} y={y - height * 0.5} fill="white" textAnchor="middle" verticalAnchor="middle">
      {payload.value}
    </Text>
  </>
);

const YAxisTick = ({
  x, y, width, payload, index,
}) => (
  <>
    <Rectangle
      x={x - width}
      y={y - lineHeight * 0.5}
      width={5000}
      // because the height passed to the y-axis is the height of the entire chart
      // we calculate it ourselves
      height={lineHeight}
      fill={index % 2 === 1 ? '#e9ecef' : '#dce0e3'}
      stroke="white"
      strokeWidth={1}
    />
    <Text x={x - width * 0.5} y={y} fill="#5b636a" textAnchor="middle" verticalAnchor="middle">
      {payload.value}
    </Text>
  </>
);

const StyledToolTip = styled.div`
  background: white;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.08);
`;

const TooltipContent = ({ active, payload, domains }) => {
  if (active) {
    return (
      <StyledToolTip>
        {payload
          .sort((a, b) => a.value - b.value)
          .map(({ dataKey, value, color }) => (
            <div key={dataKey} style={{ color }}>
              {`${value}: ${convertDataKey(dataKey, domains)}`}
            </div>
          ))}
      </StyledToolTip>
    );
  }

  return null;
};

const EstimationGraph = ({
  className, estimations, domains, children,
}) => {
  const data = useMemo(() => convertData(estimations, domains), [estimations, domains]);
  const [chartWidth, setChartWidth] = useState(500);
  const element = useRef();
  const yTicks = domains.map((domain, index) => index + 1);
  const chartHeight = lineHeight * (yTicks.length + 1);

  useEffect(() => {
    const resizeHandler = () => {
      if (element.current) {
        setChartWidth(element.current.clientWidth);
      }
    };
    resizeHandler();
    window.addEventListener('resize', resizeHandler);

    return () => window.removeEventListener('resize', resizeHandler);
  }, [element]);

  return (
    <Card className={className} forwardRef={element}>
      <CardBody className="p-0">
        <LineChart width={chartWidth} height={chartHeight} data={data} margin={zeroMargin}>
          <XAxis
            dataKey="step"
            ticks={Object.keys(estimations)}
            height={lineHeight}
            axisLine={false}
            tickLine={false}
            tickSize={0}
            orientation="top"
            tick={XAxisTick}
            margin={zeroMargin}
          />
          <YAxis
            domain={[0.5, 'dataMax + 0.5']}
            ticks={yTicks}
            axisLine={false}
            tickLine={false}
            tickSize={0}
            width={50}
            height={lineHeight}
            tick={YAxisTick}
            margin={zeroMargin}
            reversed
          />

          <Tooltip
            content={<TooltipContent domains={domains} />}
            itemSorter={item => item.value}
            labelFormatter={() => ''}
          />
          {domains.map(domain => (
            <Line
              key={domain.slug}
              type="monotone"
              dataKey={domain.slug}
              stroke={getDomainColor(domain)}
              activeDot={{ r: 10, stroke: 'black' }}
            />
          ))}
        </LineChart>
        {children}
      </CardBody>
    </Card>
  );
};

export default EstimationGraph;
